import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import SafeHtml from '@/components/base/SafeHtml';
import { useContent } from '@/context/ContentContext';
import type { CMSPage, PageSection, PageSEO } from '@/mocks/contentManagement';

type EditorTab = 'content' | 'seo' | 'preview';

const groupColors: Record<string, string> = {
  'Main Pages': 'bg-primary-500/15 text-primary-700',
  'Listings': 'bg-accent-500/15 text-accent-700',
  'Services': 'bg-teal-500/15 text-teal-500',
  'Knowledge': 'bg-secondary-500/15 text-secondary-700',
  'Location': 'bg-rose-500/15 text-rose-400',
  'Membership': 'bg-green-500/15 text-green-500',
};

export default function ContentPage() {
  const {
    pages: pagesRecord,
    loading,
    saveSection: ctxSaveSection,
    savePage: ctxSavePage,
    markDirty: ctxMarkDirty,
    discardChanges: ctxDiscardChanges,
    isDirty: ctxIsDirty,
    refresh,
  } = useContent();

  const pagesArray = useMemo(() => Object.values(pagesRecord), [pagesRecord]);

  const [selectedPageKey, setSelectedPageKey] = useState<string>('');
  const [activeTab, setActiveTab] = useState<EditorTab>('content');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dirtySEO, setDirtySEO] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const [showSource, setShowSource] = useState(false);
  const [sourceValue, setSourceValue] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cmsFileInputRef = useRef<HTMLInputElement>(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Auto-select first page when pages load
  useEffect(() => {
    if (!loading && pagesArray.length > 0 && !selectedPageKey) {
      setSelectedPageKey(pagesArray[0].pageKey);
    }
  }, [loading, pagesArray, selectedPageKey]);

  const selectedPage = useMemo(
    () => pagesRecord[selectedPageKey] || null,
    [pagesRecord, selectedPageKey]
  );

  const selectedSection = useMemo(
    () => selectedPage?.sections.find(s => s.id === selectedSectionId) || null,
    [selectedPage, selectedSectionId]
  );

  const filteredPages = useMemo(() => {
    if (!searchQuery.trim()) return pagesArray;
    const q = searchQuery.toLowerCase();
    return pagesArray.filter(p =>
      p.pageName.toLowerCase().includes(q) ||
      p.group.toLowerCase().includes(q) ||
      p.route.toLowerCase().includes(q)
    );
  }, [pagesArray, searchQuery]);

  const groupedPages = useMemo(() => {
    const groups: Record<string, CMSPage[]> = {};
    filteredPages.forEach(p => {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    });
    return groups;
  }, [filteredPages]);

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setActionMsg({ type, text });
    setTimeout(() => setActionMsg(null), 2800);
  }, []);

  const selectPage = useCallback((pageKey: string) => {
    setSelectedPageKey(pageKey);
    const page = pagesRecord[pageKey];
    if (page && page.sections.length > 0) {
      setSelectedSectionId(page.sections[0].id);
    } else {
      setSelectedSectionId(null);
    }
    setDirtySEO(false);
  }, [pagesRecord]);

  const selectSection = useCallback((sectionId: string) => {
    setSelectedSectionId(sectionId);
    setShowSource(false);
    // Reset image URL to match newly selected section
    const page = pagesRecord[selectedPageKey];
    const sec = page?.sections.find(s => s.id === sectionId);
    if (sec) setImageUrl(sec.content);
  }, [selectedPageKey, pagesRecord]);

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  }, []);

  // ── Image Section Handlers ───────────────────────
  const handleImageUrlChange = useCallback((url: string) => {
    setImageUrl(url);
    if (!selectedSection || !selectedPage) return;
    if (url !== selectedSection.content) {
      ctxMarkDirty(selectedPage.pageKey, selectedSection.id, url);
    }
  }, [selectedSection, selectedPage, ctxMarkDirty]);

  const handleSaveImageSection = useCallback(async () => {
    if (!selectedPage || !selectedSection) return;
    const updatedSection: PageSection = { ...selectedSection, content: imageUrl };
    ctxMarkDirty(selectedPage.pageKey, selectedSection.id, imageUrl);
    await ctxSaveSection(selectedPage.pageKey, updatedSection);
    showToast('success', `"${selectedSection.name}" image URL saved to server.`);
  }, [selectedPage, selectedSection, imageUrl, ctxSaveSection, ctxMarkDirty, showToast]);

  // ── CMS Image File Upload ────────────────────────
  const handleCmsImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('images', file);

      const token = localStorage.getItem('saas_token');
      const tenantId = localStorage.getItem('saas_tenant_id');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = 'Bearer ' + token;
      if (tenantId) headers['X-Tenant-ID'] = tenantId;

      const API_BASE_MEDIA = import.meta.env.VITE_PUBLIC_API_URL || '';
      const res = await fetch(`${API_BASE_MEDIA}/api/media/upload`, { method: 'POST', headers, body: formData });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Upload failed');

      const uploadedUrl = json.data?.files?.[0];
      if (uploadedUrl) {
        setImageUrl(uploadedUrl);
        if (selectedSection && selectedPage) {
          ctxMarkDirty(selectedPage.pageKey, selectedSection.id, uploadedUrl);
        }
        showToast('success', 'Image uploaded! Click Save Section to apply.');
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setImageUploading(false);
      if (cmsFileInputRef.current) cmsFileInputRef.current.value = '';
    }
  }, [selectedSection, selectedPage, ctxMarkDirty, showToast]);
  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  const handleInsertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) execCommand('createLink', url);
  }, [execCommand]);

  const handleInsertImage = useCallback(() => {
    const url = prompt('Enter image URL:');
    if (url) execCommand('insertImage', url);
  }, [execCommand]);

  const handleEditorInput = useCallback(() => {
    if (!selectedSection || !selectedPage) return;
    const html = editorRef.current?.innerHTML || '';
    const isDiff = html !== selectedSection.content;
    if (isDiff) {
      ctxMarkDirty(selectedPage.pageKey, selectedSection.id, html);
    }
  }, [selectedSection, selectedPage, ctxMarkDirty]);

  // Sync editor content when section changes
  useEffect(() => {
    if (editorRef.current && selectedSection) {
      editorRef.current.innerHTML = selectedSection.content;
    }
  }, [selectedSection?.id]);

  // ── Save Section via Context ────────────────────
  const handleSaveSection = useCallback(async () => {
    if (!selectedPage || !selectedSection) return;
    const html = editorRef.current?.innerHTML || selectedSection.content;
    const updatedSection: PageSection = { ...selectedSection, content: html };
    // Mark dirty with latest content first
    ctxMarkDirty(selectedPage.pageKey, selectedSection.id, html);
    await ctxSaveSection(selectedPage.pageKey, updatedSection);
    showToast('success', `"${selectedSection.name}" saved to server.`);
  }, [selectedPage, selectedSection, ctxSaveSection, ctxMarkDirty, showToast]);

  // ── Save All Sections via Context ───────────────
  const handleSaveAll = useCallback(async () => {
    if (!selectedPage) return;
    // For the currently selected section, capture live editor content
    let allSections = [...selectedPage.sections];
    if (selectedSection && editorRef.current) {
      const html = editorRef.current.innerHTML;
      allSections = allSections.map(s =>
        s.id === selectedSection.id ? { ...s, content: html } : s
      );
    }
    await ctxSavePage(selectedPage.pageKey, allSections);
    showToast('success', `All changes on "${selectedPage.pageName}" saved to server.`);
  }, [selectedPage, selectedSection, ctxSavePage, showToast]);

  // ── Toggle Source View ──────────────────────────
  const toggleSource = useCallback(() => {
    if (!showSource && editorRef.current) {
      setSourceValue(editorRef.current.innerHTML);
    }
    if (showSource && editorRef.current) {
      editorRef.current.innerHTML = sourceValue;
      handleEditorInput();
    }
    setShowSource(!showSource);
  }, [showSource, sourceValue, handleEditorInput]);

  // ── Discard Section ─────────────────────────────
  const handleDiscardSection = useCallback(() => {
    if (!selectedSection || !selectedPage) return;
    ctxDiscardChanges(selectedPage.pageKey, selectedSection.id);
    if (selectedSection.type === 'image') {
      setImageUrl(selectedSection.content);
    } else if (editorRef.current) {
      editorRef.current.innerHTML = selectedSection.content;
    }
    showToast('success', 'Changes discarded.');
  }, [selectedSection, selectedPage, ctxDiscardChanges, showToast]);

  // ── SEO Form ────────────────────────────────────
  const [seoForm, setSeoForm] = useState<PageSEO>({ title: '', description: '', keywords: '' });

  useEffect(() => {
    if (selectedPage) {
      setSeoForm({ ...selectedPage.seo });
      setDirtySEO(false);
    }
  }, [selectedPage?.pageKey]);

  const updateSeoField = useCallback((field: keyof PageSEO, value: string) => {
    setSeoForm(prev => {
      const next = { ...prev, [field]: value };
      if (selectedPage) {
        const orig = selectedPage.seo[field];
        const allSame = (Object.keys(next) as (keyof PageSEO)[]).every(k => next[k] === selectedPage.seo[k]);
        setDirtySEO(!allSame);
      }
      return next;
    });
  }, [selectedPage]);

  const handleSaveSEO = useCallback(async () => {
    if (!selectedPage) return;
    // SEO is stored as part of page config, not as a regular section.
    // For now, save it via a special section with id 'seo-meta'
    const seoData = JSON.stringify(seoForm);
    const seoSection: PageSection = {
      id: 'seo-meta',
      name: 'SEO Metadata',
      type: 'text',
      content: seoData,
      lastModified: new Date().toISOString().split('T')[0],
      status: 'published',
    };
    await ctxSaveSection(selectedPage.pageKey, seoSection);
    setDirtySEO(false);
    showToast('success', 'SEO metadata saved to server.');
  }, [selectedPage, seoForm, ctxSaveSection, showToast]);

  const handleDiscardSEO = useCallback(() => {
    if (!selectedPage) return;
    setSeoForm({ ...selectedPage.seo });
    setDirtySEO(false);
    showToast('success', 'SEO changes discarded.');
  }, [selectedPage, showToast]);

  // ── Keyboard shortcut ────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (activeTab === 'seo' && dirtySEO) handleSaveSEO();
        else if (activeTab === 'content' && selectedSection && selectedPage && ctxIsDirty(selectedPage.pageKey, selectedSection.id)) {
          handleSaveSection();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab, dirtySEO, selectedSection, selectedPage, ctxIsDirty, handleSaveSEO, handleSaveSection]);

  const totalSections = pagesArray.reduce((sum, p) => sum + p.sections.length, 0);
  const totalDirty = pagesArray.reduce((sum, p) => {
    const dirtyCount = p.sections.filter(s => ctxIsDirty(p.pageKey, s.id)).length;
    return sum + dirtyCount;
  }, 0) + (dirtySEO ? 1 : 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-32">
          <div className="text-center space-y-3">
            <div className="w-10 h-10 rounded-full border-2 border-accent-500 border-t-transparent animate-spin mx-auto" />
            <p className="text-sm text-foreground-500">Loading content...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg transition-all ${
            actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
          }`}>
            {actionMsg.text}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Content Management</h1>
            <p className="text-xs text-foreground-500">
              {pagesArray.length} pages &middot; {totalSections} editable sections
              {totalDirty > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-accent-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                  {totalDirty} unsaved change{totalDirty !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-background-200/60 bg-white text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap transition-all"
              title="Refresh from server"
            >
              <i className="ri-refresh-line" /> Refresh
            </button>
            <button
              onClick={handleSaveAll}
              disabled={totalDirty === 0}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-save-line" /> Save All Changes
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Pages', value: pagesArray.length, icon: 'ri-pages-line', color: 'primary' },
            { label: 'Content Sections', value: totalSections, icon: 'ri-file-text-line', color: 'accent' },
            { label: 'Published', value: pagesArray.reduce((s, p) => s + p.sections.filter(sec => sec.status === 'published').length, 0), icon: 'ri-check-double-line', color: 'teal' },
            { label: 'Drafts', value: pagesArray.reduce((s, p) => s + p.sections.filter(sec => sec.status === 'draft').length, 0), icon: 'ri-draft-line', color: 'secondary' },
          ].map(stat => (
            <div key={stat.label} className="rounded-xl border border-background-200/60 bg-white p-3">
              <div className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  stat.color === 'primary' ? 'bg-primary-100 text-primary-700' :
                  stat.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                  stat.color === 'teal' ? 'bg-teal-500/15 text-teal-500' :
                  'bg-secondary-100 text-secondary-700'
                }`}>
                  <i className={`${stat.icon} text-xs`} />
                </span>
                <div>
                  <p className="text-[10px] text-foreground-500 uppercase tracking-wider">{stat.label}</p>
                  <p className="font-heading text-lg font-bold text-foreground-950">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* ── LEFT SIDEBAR: Pages ── */}
          <div className="lg:col-span-1 space-y-3">
            {/* Search */}
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search pages..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50"
              />
            </div>

            {/* Page list grouped */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden max-h-[calc(100vh-340px)] overflow-y-auto">
              {Object.keys(groupedPages).length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <i className="ri-search-line text-2xl text-foreground-400 mb-2 block" />
                  <p className="text-xs text-foreground-500">No pages match "{searchQuery}"</p>
                </div>
              ) : (
                Object.entries(groupedPages).map(([group, groupPages]) => (
                  <div key={group}>
                    <button
                      onClick={() => toggleGroup(group)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${groupColors[group] || 'bg-background-200/50 text-foreground-500'}`}>
                          {group}
                        </span>
                        <span className="text-[10px] text-foreground-500">{groupPages.length}</span>
                      </div>
                      {collapsedGroups[group] ? (
                        <i className="ri-arrow-right-s-line text-foreground-500 text-sm" />
                      ) : (
                        <i className="ri-arrow-down-s-line text-foreground-500 text-sm" />
                      )}
                    </button>
                    {!collapsedGroups[group] && (
                      <div className="divide-y divide-background-100">
                        {groupPages.map(page => (
                          <button
                            key={page.pageKey}
                            onClick={() => selectPage(page.pageKey)}
                            className={`w-full text-left px-4 py-3 transition-colors cursor-pointer group ${
                              selectedPageKey === page.pageKey
                                ? 'bg-accent-50 border-l-2 border-accent-500'
                                : 'hover:bg-background-50 border-l-2 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                selectedPageKey === page.pageKey
                                  ? 'bg-accent-100 text-accent-700'
                                  : 'bg-background-100 text-foreground-500 group-hover:text-foreground-700'
                              }`}>
                                <i className={`${page.icon} text-xs`} />
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm truncate ${selectedPageKey === page.pageKey ? 'text-accent-700 font-semibold' : 'text-foreground-950'}`}>
                                  {page.pageName}
                                </p>
                                <p className="text-[10px] text-foreground-500">{page.route}</p>
                              </div>
                              <span className="text-[10px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                {page.sections.length}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── RIGHT: Editor ── */}
          <div className="lg:col-span-3">
            {selectedPage ? (
              <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
                {/* Page Header */}
                <div className="px-5 py-4 border-b border-background-200/60">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${groupColors[selectedPage.group] || 'bg-background-200/50 text-foreground-500'}`}>
                        <i className={`${selectedPage.icon} text-sm`} />
                      </span>
                      <div>
                        <h3 className="text-base font-semibold text-foreground-950">{selectedPage.pageName}</h3>
                        <p className="text-[11px] text-foreground-500">{selectedPage.route} &middot; {selectedPage.sections.length} sections</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 px-5 py-2 border-b border-background-200/60 bg-background-50">
                  {[
                    { key: 'content' as EditorTab, label: 'Content Sections', icon: 'ri-file-edit-line' },
                    { key: 'seo' as EditorTab, label: 'SEO Metadata', icon: 'ri-search-line' },
                    { key: 'preview' as EditorTab, label: 'Preview', icon: 'ri-eye-line' },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs whitespace-nowrap cursor-pointer transition-all ${
                        activeTab === tab.key
                          ? 'bg-accent-100 text-accent-700 font-semibold'
                          : 'text-foreground-500 hover:text-foreground-950'
                      }`}
                    >
                      <i className={`${tab.icon} text-[11px]`} />
                      {tab.label}
                      {tab.key === 'seo' && dirtySEO && (
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                      )}
                      {tab.key === 'content' && pagesArray.reduce((s, p) => s + p.sections.filter(sec => ctxIsDirty(p.pageKey, sec.id)).length, 0) > 0 && (
                        <span className="text-[9px] bg-accent-500 text-white px-1 rounded-full">
                          {pagesArray.reduce((s, p) => s + p.sections.filter(sec => ctxIsDirty(p.pageKey, sec.id)).length, 0)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* ── TAB: Content Sections ── */}
                {activeTab === 'content' && (
                  <div className="grid grid-cols-1 md:grid-cols-4">
                    {/* Sections sidebar */}
                    <div className="md:col-span-1 border-r border-background-200/60 max-h-[500px] overflow-y-auto">
                      {selectedPage.sections.map(section => (
                        <button
                          key={section.id}
                          onClick={() => selectSection(section.id)}
                          className={`w-full text-left px-4 py-3 transition-colors cursor-pointer border-l-2 ${
                            selectedSectionId === section.id
                              ? 'bg-accent-50 border-accent-500'
                              : 'border-transparent hover:bg-background-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className={`text-xs truncate ${selectedSectionId === section.id ? 'text-accent-700 font-semibold' : 'text-foreground-950'}`}>
                              {section.name}
                            </p>
                            {ctxIsDirty(selectedPage.pageKey, section.id) && (
                              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-[10px] text-foreground-500 mt-0.5">
                            {section.type === 'html' ? 'Rich Text' : section.type === 'image' ? 'Image' : 'Plain Text'}
                            <span className={`ml-2 inline-flex items-center gap-1 ${
                              section.status === 'published' ? 'text-teal-500' : 'text-foreground-500'
                            }`}>
                              <span className={`w-1 h-1 rounded-full ${section.status === 'published' ? 'bg-teal-500' : 'bg-foreground-400'}`} />
                              {section.status}
                            </span>
                          </p>
                        </button>
                      ))}
                    </div>

                    {/* Editor area */}
                    <div className="md:col-span-3 p-5 space-y-3">
                      {selectedSection ? (
                        <>
                          {/* Section info */}
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-foreground-950">{selectedSection.name}</h4>
                              <p className="text-[10px] text-foreground-500">
                                Type: {selectedSection.type} &middot; Last modified: {selectedSection.lastModified}
                              </p>
                            </div>
                            {selectedSection.type !== 'image' && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={toggleSource}
                                className={`px-2.5 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all ${
                                  showSource
                                    ? 'bg-foreground-950 text-white'
                                    : 'border border-background-200/60 text-foreground-500 hover:text-foreground-950'
                                }`}
                              >
                                <i className={`${showSource ? 'ri-code-line' : 'ri-code-s-slash-line'} mr-1`} />
                                {showSource ? 'Visual' : 'Source'}
                              </button>
                            </div>
                            )}
                          </div>

                          {/* Rich Text Toolbar */}
                          {!showSource && (
                            <div className="flex items-center gap-0.5 flex-wrap p-2 rounded-xl bg-background-50 border border-background-200/60">
                              <div className="flex items-center gap-0.5 pr-2 mr-2 border-r border-background-200/60">
                                <button onClick={() => execCommand('bold')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-sm" title="Bold (Ctrl+B)"><strong>B</strong></button>
                                <button onClick={() => execCommand('italic')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-sm" title="Italic (Ctrl+I)"><em>I</em></button>
                                <button onClick={() => execCommand('underline')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-sm" title="Underline (Ctrl+U)"><u>U</u></button>
                              </div>
                              <div className="flex items-center gap-0.5 pr-2 mr-2 border-r border-background-200/60">
                                <button onClick={() => execCommand('formatBlock', '<h1>')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-[10px] font-bold" title="Heading 1">H1</button>
                                <button onClick={() => execCommand('formatBlock', '<h2>')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-[10px] font-bold" title="Heading 2">H2</button>
                                <button onClick={() => execCommand('formatBlock', '<h3>')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-[10px] font-bold" title="Heading 3">H3</button>
                                <button onClick={() => execCommand('formatBlock', '<p>')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer text-[10px]" title="Paragraph">P</button>
                              </div>
                              <div className="flex items-center gap-0.5 pr-2 mr-2 border-r border-background-200/60">
                                <button onClick={() => execCommand('insertUnorderedList')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="Bullet List"><i className="ri-list-unordered text-sm" /></button>
                                <button onClick={() => execCommand('insertOrderedList')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="Numbered List"><i className="ri-list-ordered text-sm" /></button>
                              </div>
                              <div className="flex items-center gap-0.5 pr-2 mr-2 border-r border-background-200/60">
                                <button onClick={handleInsertLink} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="Insert Link"><i className="ri-link text-sm" /></button>
                                <button onClick={handleInsertImage} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="Insert Image"><i className="ri-image-line text-sm" /></button>
                              </div>
                              <div className="flex items-center gap-0.5">
                                <button onClick={() => execCommand('removeFormat')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="Clear Formatting"><i className="ri-format-clear text-sm" /></button>
                              </div>
                            </div>
                          )}

                          {/* Content area */}
                          {selectedSection.type === 'image' ? (
                            <div className="space-y-4">
                              {/* Image Preview */}
                              <div className="w-full h-52 rounded-xl bg-background-100 border border-background-200/60 overflow-hidden">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={selectedSection.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <div className={`w-full h-full flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}>
                                  <div className="text-center">
                                    <i className="ri-image-add-line text-3xl text-foreground-400 mb-2 block" />
                                    <p className="text-xs text-foreground-500">No image URL set</p>
                                  </div>
                                </div>
                              </div>

                              {/* URL Input */}
                              <div>
                                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">
                                  Image URL
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    ref={imageInputRef}
                                    type="text"
                                    value={imageUrl}
                                    onChange={e => handleImageUrlChange(e.target.value)}
                                    placeholder="https://readdy.ai/api/search-image?..."
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 font-mono"
                                  />
                                  <input
                                    ref={cmsFileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleCmsImageUpload}
                                    className="hidden"
                                  />
                                  <button
                                    onClick={() => cmsFileInputRef.current?.click()}
                                    disabled={imageUploading}
                                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-background-200/60 bg-white text-sm text-foreground-500 hover:text-foreground-950 hover:border-accent-200 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-all"
                                  >
                                    <i className={`${imageUploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'} text-sm`} />
                                    {imageUploading ? 'Uploading...' : 'Upload'}
                                  </button>
                                </div>
                                <p className="text-[10px] text-foreground-500 mt-1.5">
                                  <i className="ri-information-line mr-1" />
                                  Paste an image URL or click Upload to select from your device. Uploaded files go to the Media Library.
                                </p>
                              </div>
                            </div>
                          ) : showSource ? (
                            <textarea
                              value={sourceValue}
                              onChange={e => setSourceValue(e.target.value)}
                              rows={14}
                              className="w-full px-4 py-3 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 font-mono focus:outline-none focus:border-accent-500/50 resize-none"
                              placeholder="<p>Enter HTML content here...</p>"
                            />
                          ) : (
                            <div
                              ref={editorRef}
                              contentEditable
                              onInput={handleEditorInput}
                              className="w-full min-h-[280px] px-4 py-3 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 prose prose-sm max-w-none overflow-y-auto"
                              style={{ maxHeight: '400px' }}
                              suppressContentEditableWarning
                            />
                          )}

                          {/* Action bar */}
                          <div className="flex items-center justify-between pt-2 border-t border-background-200/60">
                            <div className="flex items-center gap-2">
                              {ctxIsDirty(selectedPage.pageKey, selectedSection.id) ? (
                                <span className="flex items-center gap-1.5 text-[10px] text-accent-700">
                                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                                  Unsaved changes
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-[10px] text-teal-500">
                                  <i className="ri-check-line text-xs" />
                                  Saved
                                </span>
                              )}
                              {selectedSection.type !== 'image' && (
                                <span className="text-[10px] text-foreground-500">Ctrl+S to save</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleDiscardSection}
                                disabled={!ctxIsDirty(selectedPage.pageKey, selectedSection.id)}
                                className="px-3 py-2 rounded-lg border border-background-200/60 text-xs text-foreground-500 hover:text-foreground-950 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all"
                              >
                                <i className="ri-arrow-go-back-line mr-1" /> Discard
                              </button>
                              <button
                                onClick={selectedSection.type === 'image' ? handleSaveImageSection : handleSaveSection}
                                disabled={!ctxIsDirty(selectedPage.pageKey, selectedSection.id)}
                                className="px-4 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all"
                              >
                                <i className="ri-save-line mr-1" /> Save Section
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="py-16 text-center">
                          <i className="ri-file-edit-line text-4xl text-foreground-400 mb-3 block" />
                          <p className="text-sm text-foreground-500">Select a content section from the left panel to edit</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── TAB: SEO Metadata ── */}
                {activeTab === 'seo' && (
                  <div className="p-6 space-y-5">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1">SEO Metadata</h4>
                      <p className="text-xs text-foreground-500">Manage how this page appears in search engines and social shares.</p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">
                          Page Title <span className="text-foreground-400">({seoForm.title.length}/60 recommended)</span>
                        </label>
                        <input
                          type="text"
                          value={seoForm.title}
                          onChange={e => updateSeoField('title', e.target.value)}
                          maxLength={120}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50"
                          placeholder="Enter page title..."
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">
                          Meta Description <span className="text-foreground-400">({seoForm.description.length}/160 recommended)</span>
                        </label>
                        <textarea
                          value={seoForm.description}
                          onChange={e => updateSeoField('description', e.target.value)}
                          rows={3}
                          maxLength={200}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 resize-none"
                          placeholder="Enter meta description..."
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">
                          Keywords <span className="text-foreground-400">(comma-separated)</span>
                        </label>
                        <input
                          type="text"
                          value={seoForm.keywords}
                          onChange={e => updateSeoField('keywords', e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50"
                          placeholder="keyword1, keyword2, keyword3..."
                        />
                      </div>

                      {/* SEO Preview Card */}
                      <div className="rounded-xl border border-background-200/60 bg-background-50 p-4 space-y-2">
                        <p className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Search Engine Preview</p>
                        <div>
                          <p className="text-sm font-medium text-primary-700 truncate">{seoForm.title || 'Untitled Page'}</p>
                          <p className="text-xs text-teal-500 truncate">{window.location.origin}{selectedPage.route}</p>
                          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{seoForm.description || 'No description set.'}</p>
                        </div>
                      </div>
                    </div>

                    {/* SEO Action bar */}
                    <div className="flex items-center justify-between pt-3 border-t border-background-200/60">
                      <div className="flex items-center gap-2">
                        {dirtySEO ? (
                          <span className="flex items-center gap-1.5 text-[10px] text-accent-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                            Unsaved changes
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-[10px] text-teal-500">
                            <i className="ri-check-line text-xs" /> Saved
                          </span>
                        )}
                        <span className="text-[10px] text-foreground-500">Ctrl+S to save</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleDiscardSEO}
                          disabled={!dirtySEO}
                          className="px-3 py-2 rounded-lg border border-background-200/60 text-xs text-foreground-500 hover:text-foreground-950 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all"
                        >
                          <i className="ri-arrow-go-back-line mr-1" /> Discard
                        </button>
                        <button
                          onClick={handleSaveSEO}
                          disabled={!dirtySEO}
                          className="px-4 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all"
                        >
                          <i className="ri-save-line mr-1" /> Save SEO
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── TAB: Preview ── */}
                {activeTab === 'preview' && (
                  <div className="p-6 space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1">Page Content Preview</h4>
                      <p className="text-xs text-foreground-500">See how your content will appear on the live page.</p>
                    </div>

                    <div className="rounded-xl border-2 border-background-200/60 overflow-hidden">
                      <div className="flex items-center gap-1.5 px-3 py-2 bg-background-100 border-b border-background-200/60">
                        <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                        <span className="w-2.5 h-2.5 rounded-full bg-accent-500" />
                        <span className="w-2.5 h-2.5 rounded-full bg-teal-500" />
                        <div className="ml-3 flex-1 px-3 py-1 rounded-md bg-white border border-background-200/60 text-[10px] text-foreground-500 truncate">
                          {window.location.origin}{selectedPage.route}
                        </div>
                      </div>

                      <div className="p-6 bg-white max-h-[500px] overflow-y-auto space-y-6">
                        {selectedPage.sections.map(section => (
                          <div key={section.id} className="space-y-1">
                            <p className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider bg-background-50 px-2 py-0.5 rounded inline-block">
                              {section.name}
                            </p>
                            {section.type === 'image' ? (
                              <div className="w-full h-40 bg-background-100 rounded-lg flex items-center justify-center">
                                <i className="ri-image-line text-2xl text-foreground-400" />
                              </div>
                            ) : (
                              <SafeHtml
                                className="text-sm text-foreground-950 prose prose-sm max-w-none"
                                html={ctxIsDirty(selectedPage.pageKey, section.id) ? (editorRef.current?.innerHTML || section.content) : section.content}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-background-200/60 bg-white p-16 text-center">
                <i className="ri-pages-line text-5xl text-foreground-400 mb-4 block" />
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">Select a Page to Edit</h3>
                <p className="text-sm text-foreground-500 max-w-md mx-auto">
                  Choose any page from the sidebar to start editing its content sections, SEO metadata, and preview how it looks.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}